import type { ReactElement } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { radius, spacing, typography } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { resolveTheme } from "../theme";

// The screen a render crash falls back to, on BOTH hosts. Lifted out of
// apps/web-rnw/src/crashBoundary.tsx on 2026-09-11 (CRASH-2): mobile had no
// boundary at all after the crash reporter was removed on 09-10, and a copy
// here would have been the second one of a screen that already existed.
//
// Provider-independent ON PURPOSE: the ThemeProvider (or anything above it)
// may be the thing that crashed, so the theme is resolved with the PURE
// resolveTheme() — never ThemeContext. `prefersDark` is a prop because the
// two hosts read the system scheme differently (matchMedia vs Appearance),
// and neither belongs in shared code.
//
// The `web.crash.*` key namespace is historic: these strings shipped in five
// locales under that name and renaming them would move translations for no
// user-visible gain, so the keys keep it even though mobile reads them too.

export type CrashFallbackProps = {
  error?: unknown;
  prefersDark: boolean;
  /** What the action actually DOES. The copy follows it: "reload" is a real
   *  page reload, "retry" only re-renders the tree, and a screen that says
   *  "reload" where nothing reloads teaches the athlete the wrong thing —
   *  worse, it hides that a deterministic crash is about to come straight
   *  back. The boundary derives this from the host, never the fallback. */
  recovery: "reload" | "retry";
  onAction: () => void;
};

export function CrashFallback({
  error,
  prefersDark,
  recovery,
  onAction,
}: CrashFallbackProps): ReactElement {
  const { t } = useTranslation();
  const theme = resolveTheme(prefersDark);
  const p = theme.palette;

  // RW-22 — the fallback SHOWS what broke. Without it the screen said
  // "something went wrong" and threw the one useful fact away, which turned
  // every report into "open devtools and read me the console". Deliberately
  // not gated behind DEV: the people hitting this ARE the testers, and a
  // crash they can quote is worth more than a tidier crash they can't.
  const detail =
    error instanceof Error
      ? [error.message, error.stack?.split("\n").slice(1, 4).join("\n")]
          .filter(Boolean)
          .join("\n")
      : error !== undefined && error !== null
        ? String(error)
        : null;

  return (
    <View
      // A screen-reader / magnifier user must be TOLD the crash screen
      // appeared, not left to notice it.
      accessibilityRole="alert"
      // On web the role alone is an assertive ARIA live region. On native it
      // is NOT announced by itself, so Android needs this second prop — RNW
      // maps it to aria-live, so the two agree rather than diverge.
      accessibilityLiveRegion="assertive"
      style={{
        flex: 1,
        backgroundColor: p.bg,
        alignItems: "center",
        justifyContent: "center",
        padding: spacing.s6,
      }}
    >
      <View
        style={{
          maxWidth: 420,
          width: "100%",
          backgroundColor: p.surface,
          borderRadius: radius.r3,
          borderWidth: 1,
          borderColor: p.borderStrong,
          padding: spacing.s6,
          gap: spacing.s4,
        }}
      >
        <Text
          accessibilityRole="header"
          style={{
            color: p.ink,
            fontSize: typography.sizes.lg,
            fontWeight: "700",
          }}
        >
          {t("errors.generic", { defaultValue: "Something went wrong." })}
        </Text>
        <Text
          style={{
            color: p.ink2,
            fontSize: typography.sizes.md,
            lineHeight: typography.sizes.md * typography.leading.base,
          }}
        >
          {recovery === "reload"
            ? t("web.crash.body", { defaultValue: "The app hit an unexpected error. Your saved data is safe. Reload to continue." })
            : t("web.crash.retryBody", {
                defaultValue: "The app hit an unexpected error. Your saved data is safe. Try again, and if this screen comes back, send us the message below.",
              })}
        </Text>
        {detail !== null ? (
          // selectable: the whole point is that it can be copied into a bug
          // report. Monospace because it is a stack, and capped in height
          // because a long frame must not push the action off the card.
          <View
            style={{
              backgroundColor: p.surface2,
              borderRadius: radius.r2,
              borderWidth: 1,
              borderColor: p.border,
              maxHeight: 180,
              padding: spacing.s3,
            }}
          >
            <Text
              selectable
              style={{
                color: p.ink2,
                fontFamily: "monospace",
                fontSize: typography.sizes.sm,
                lineHeight: typography.sizes.sm * typography.leading.base,
              }}
            >
              {detail}
            </Text>
          </View>
        ) : null}
        <Button
          theme={theme}
          variant="secondary"
          label={
            recovery === "reload"
              ? t("web.crash.reload", { defaultValue: "Reload" })
              : t("web.crash.retry", { defaultValue: "Try again" })
          }
          onPress={onAction}
          style={{ alignSelf: "flex-start" }}
        />
      </View>
    </View>
  );
}
